import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Button, Card, Col, Form, Row, Table } from 'react-bootstrap'
import {
  useAdminAnalyticConfigKeys,
  useApplyAdminAnalyticConfigDefaults,
  useCreateAdminAnalyticConfigKey,
  useDeleteAdminAnalyticConfigKey,
  useUpdateAdminAnalyticConfigKey,
  useUpdateAdminAnalyticConfigValues
} from 'api/hooks/use-admin-analytic-configs'
import AppBadge from 'shared/badges/app-badge'
import { ActionButton } from 'shared/buttons'
import CenteredSpinner from 'shared/centered-spinner'
import { configValueError, formatConfigValue, parseConfigValue } from './format'

const DATATYPES = ['number', 'integer', 'percent', 'string', 'array', 'boolean']

const errorMessage = (error) => error?.response?.data?.message || error?.message

const ReadBy = ({ configKey }) => {
  if (configKey.source === 'recipe') return <AppBadge variant="ai">Recipe</AppBadge>
  if (configKey.analytics_count > 1) {
    return <AppBadge variant="info">{configKey.analytics_count} analytics</AppBadge>
  }
  return <span className="small text-muted">This analytic</span>
}

// Which keys the analytic's deployed code reads comes from the last sync
// (ConfigKeySync, run from the analytics index), which also attaches any it
// reads that aren't attached yet.
const CodeSync = ({ code, configKeys }) => {
  const attached = new Set(configKeys.map((key) => key.config_key))
  const notAttached = (code?.config_keys || []).filter((key) => !attached.has(key))

  let status
  if (!code?.synced_at) {
    status = 'Not synced from the analytics code yet.'
  } else if (!code.covered) {
    status = 'This analytic’s code isn’t in the analytics repo, so which keys it reads isn’t known.'
  } else {
    status = `Synced from the analytics code ${new Date(code.synced_at).toLocaleString()}.`
  }

  return (
    <div className="mb-3" data-testid="code-sync">
      <span className="small text-muted">{status}</span>
      {notAttached.length > 0 && (
        <Alert variant="warning" className="small py-2 mt-2 mb-0">
          The code also reads {notAttached.map((key) => <code key={key} className="me-1">{key}</code>)}
          — the next sync attaches {notAttached.length === 1 ? 'it' : 'them'}.
        </Alert>
      )}
    </div>
  )
}

const DatatypeSelect = ({ value, onChange, name = 'datatype', ...props }) => (
  <Form.Select name={name} value={value} onChange={onChange} {...props}>
    {DATATYPES.map((datatype) => (
      <option key={datatype} value={datatype}>
        {datatype}
      </option>
    ))}
  </Form.Select>
)

const EMPTY_KEY = { config_key: '', label: '', datatype: 'number', description: '', default_value: '' }

const AddConfigKeyForm = ({ analyticId, availableKeys }) => {
  const [form, setForm] = useState(EMPTY_KEY)
  const [notice, setNotice] = useState(null)
  const [inputError, setInputError] = useState(null)
  const existing = availableKeys.find((key) => key.config_key === form.config_key.trim())
  const createMutation = useCreateAdminAnalyticConfigKey({
    analyticId,
    options: {
      onSuccess: (data) => {
        setForm(EMPTY_KEY)
        setNotice(`Attached ${data.config_key}; default set for ${data.municipalities_backfilled} municipalities.`)
      }
    }
  })

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setNotice(null)
    if (existing) {
      setInputError(null)
      createMutation.mutate({ config_key: existing.config_key })
      return
    }
    const invalid = configValueError(form.default_value, form.datatype)
    setInputError(invalid)
    if (invalid) return
    createMutation.mutate({ ...form, default_value: parseConfigValue(form.default_value, form.datatype) })
  }

  return (
    <Form onSubmit={handleSubmit} data-testid="add-config-key-form">
      <h3 className="h6">Attach a config variable</h3>
      <p className="small text-muted">
        The key is passed to the analytic’s Lambda in its <code>config</code> payload, so the analytic’s code must
        read it. Pick an existing key to share its definition, or type a new one to define it. Every municipality
        with this module gets the default unless it already has a value for the key.
      </p>
      <Row className="g-2 align-items-end">
        <Col md={3}>
          <Form.Group controlId="config-key-name">
            <Form.Label className="small mb-1">Key</Form.Label>
            <Form.Control
              name="config_key"
              value={form.config_key}
              onChange={handleChange}
              placeholder="e.g. min_invoice_amount"
              list="available-config-keys"
              autoComplete="off"
              required
            />
            <datalist id="available-config-keys">
              {availableKeys.map((key) => (
                <option key={key.config_key} value={key.config_key}>
                  {key.label}
                </option>
              ))}
            </datalist>
          </Form.Group>
        </Col>
        {existing ? (
          <Col md={8} data-testid="existing-key-definition">
            <div className="small">
              <strong>{existing.label || existing.config_key}</strong> · {existing.datatype} · default{' '}
              <code>{formatConfigValue(existing.default_value)}</code>
            </div>
            <div className="small text-muted">
              {existing.description} Already defined, so its definition is shared rather than copied.
            </div>
          </Col>
        ) : (
          <>
            <Col md={2}>
              <Form.Group controlId="config-key-label">
                <Form.Label className="small mb-1">Label</Form.Label>
                <Form.Control name="label" value={form.label} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group controlId="config-key-datatype">
                <Form.Label className="small mb-1">Type</Form.Label>
                <DatatypeSelect value={form.datatype} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group controlId="config-key-default">
                <Form.Label className="small mb-1">Default</Form.Label>
                <Form.Control
                  name="default_value"
                  value={form.default_value}
                  onChange={handleChange}
                  placeholder={form.datatype === 'array' ? 'a, b, c' : ''}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="config-key-description">
                <Form.Label className="small mb-1">Description</Form.Label>
                <Form.Control name="description" value={form.description} onChange={handleChange} />
              </Form.Group>
            </Col>
          </>
        )}
        <Col md={1}>
          <ActionButton
            type="submit"
            label={existing ? 'Attach' : 'Add'}
            size="md"
            loading={createMutation.isLoading}
            loadingLabel="Saving"
            className="w-100"
          />
        </Col>
      </Row>
      {(inputError || createMutation.error) && (
        <Alert variant="danger" className="mt-2 mb-0 small">
          {inputError ? `Default: ${inputError}` : errorMessage(createMutation.error)}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" className="mt-2 mb-0 small">
          {notice}
        </Alert>
      )}
    </Form>
  )
}

const definitionDraft = (configKey) => ({
  label: configKey.label || '',
  datatype: configKey.datatype,
  description: configKey.description || '',
  default_value: formatConfigValue(configKey.default_value),
  array_label: configKey.array_label || ''
})

// Only fields the admin changed are sent: re-parsing an untouched array
// default from its comma-joined text would drop blank items and split items
// containing commas.
const changedDefinition = (draft, initial) => {
  const changed = Object.keys(draft).filter((field) => draft[field] !== initial[field])
  return Object.fromEntries(changed.map((field) => [field, draft[field]]))
}

const EditDefinitionRow = ({ analyticId, configKey, onDone }) => {
  const [initial] = useState(() => definitionDraft(configKey))
  const [draft, setDraft] = useState(initial)
  const [inputError, setInputError] = useState(null)
  const updateMutation = useUpdateAdminAnalyticConfigKey({ analyticId, options: { onSuccess: onDone } })

  const handleChange = (e) => {
    const { name, value } = e.target
    setDraft((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = () => {
    const params = changedDefinition(draft, initial)
    if (Object.keys(params).length === 0) return onDone()

    if ('default_value' in params || 'datatype' in params) {
      const invalid = configValueError(draft.default_value, draft.datatype)
      setInputError(invalid && `Default: ${invalid}`)
      if (invalid) return
      const defaultValue = parseConfigValue(draft.default_value, draft.datatype)
      if (defaultValue === undefined) return setInputError('Default is required')
      params.default_value = defaultValue
    }
    updateMutation.mutate({ keyId: configKey.id, params })
  }

  const shared = configKey.analytics_count > 1

  return (
    <tr data-testid="config-key-edit-row">
      <td colSpan={6}>
        {shared && (
          <Alert variant="warning" className="small py-2">
            {configKey.analytics_count} analytics read <code>{configKey.config_key}</code>; this changes its definition
            for all of them. Municipality values are not changed.
          </Alert>
        )}
        <Row className="g-2 align-items-end">
          <Col md={2}>
            <Form.Label className="small mb-1">Label</Form.Label>
            <Form.Control size="sm" name="label" aria-label="Label" value={draft.label} onChange={handleChange} />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Type</Form.Label>
            <DatatypeSelect size="sm" aria-label="Type" value={draft.datatype} onChange={handleChange} />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Default</Form.Label>
            <Form.Control
              size="sm"
              name="default_value"
              aria-label="Default"
              value={draft.default_value}
              onChange={handleChange}
            />
          </Col>
          {draft.datatype === 'array' && (
            <Col md={2}>
              <Form.Label className="small mb-1">Item name</Form.Label>
              <Form.Control
                size="sm"
                name="array_label"
                aria-label="Item name"
                placeholder="e.g. keywords"
                value={draft.array_label}
                onChange={handleChange}
              />
            </Col>
          )}
          <Col>
            <Form.Label className="small mb-1">Description</Form.Label>
            <Form.Control
              size="sm"
              name="description"
              aria-label="Description"
              value={draft.description}
              onChange={handleChange}
            />
          </Col>
          <Col xs="auto">
            <ActionButton label="Save" onClick={handleSave} loading={updateMutation.isLoading} loadingLabel="Saving" />
            <Button size="sm" variant="light" className="text-primary ms-2" onClick={onDone}>
              Cancel
            </Button>
          </Col>
        </Row>
        {(inputError || updateMutation.error) && (
          <div className="small text-danger mt-1">{inputError || errorMessage(updateMutation.error)}</div>
        )}
      </td>
    </tr>
  )
}

const ConfigKeysTable = ({ analyticId, configKeys }) => {
  const [editingId, setEditingId] = useState(null)
  const deleteMutation = useDeleteAdminAnalyticConfigKey({ analyticId })
  const applyMutation = useApplyAdminAnalyticConfigDefaults({ analyticId })

  const handleRemove = (key) => {
    const message =
      `Detach ${key.config_key} from this analytic? If the analytic's code still reads it, it falls back to ` +
      'the default written in that code. The definition and municipality values are kept.'
    if (window.confirm(message)) {
      deleteMutation.mutate(key.id)
    }
  }

  if (configKeys.length === 0) {
    return <Alert variant="info">This analytic doesn’t read any config variables yet.</Alert>
  }

  return (
    <>
      {(deleteMutation.error || applyMutation.error) && (
        <Alert variant="danger" className="small">
          {errorMessage(deleteMutation.error || applyMutation.error)}
        </Alert>
      )}
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead>
            <tr>
              <th>Key</th>
              <th>Read by</th>
              <th>Type</th>
              <th>Default</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {configKeys.map((key) =>
              editingId && editingId === key.id ? (
                <EditDefinitionRow
                  key={key.config_key}
                  analyticId={analyticId}
                  configKey={key}
                  onDone={() => setEditingId(null)}
                />
              ) : (
                <tr key={key.config_key} data-testid="config-key-row">
                  <td>
                    <code>{key.config_key}</code>
                    {key.read_by_code === false && (
                      <AppBadge
                        variant="warning"
                        className="ms-2"
                        title="The analytic’s code doesn’t read this key, so changing it changes nothing"
                      >
                        Not read by code
                      </AppBadge>
                    )}
                    {key.label && <div className="small text-muted">{key.label}</div>}
                  </td>
                  <td>
                    <ReadBy configKey={key} />
                  </td>
                  <td>{key.datatype || <span className="text-muted">—</span>}</td>
                  <td>{formatConfigValue(key.default_value) || <span className="text-muted">—</span>}</td>
                  <td className="small">{key.description}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {key.source === 'definition' && (
                      <>
                        <Button
                          size="sm"
                          variant="light"
                          className="text-primary me-2"
                          onClick={() => setEditingId(key.id)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="light"
                          className="text-primary me-2"
                          onClick={() => applyMutation.mutate(key.id)}
                          disabled={applyMutation.isLoading}
                          title="Give municipalities without a value the default"
                        >
                          Apply default
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleRemove(key)}
                          disabled={deleteMutation.isLoading || key.read_by_code === true}
                          title={key.read_by_code ? 'The analytic’s code reads this key' : undefined}
                        >
                          Detach
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      </div>
    </>
  )
}

const MunicipalityValuesRow = ({ analyticId, municipality, configKeys }) => {
  const [draft, setDraft] = useState({})
  const [inputError, setInputError] = useState(null)
  const updateMutation = useUpdateAdminAnalyticConfigValues({
    analyticId,
    options: { onSuccess: () => setDraft({}) }
  })
  const dirty = Object.keys(draft).length > 0

  const handleSave = () => {
    const invalid = configKeys
      .filter(({ config_key: key }) => key in draft)
      .map(({ config_key: key, datatype }) => {
        const message = configValueError(draft[key], datatype)
        return message && `${key}: ${message}`
      })
      .filter(Boolean)
    setInputError(invalid.join('; ') || null)
    if (invalid.length > 0) return

    const configs = {}
    configKeys.forEach(({ config_key: key, datatype }) => {
      if (!(key in draft)) return
      const value = parseConfigValue(draft[key], datatype)
      if (value !== undefined) configs[key] = value
    })
    if (Object.keys(configs).length === 0) return setDraft({})
    updateMutation.mutate({ municipalityId: municipality.municipality_id, configs })
  }

  return (
    <tr data-testid="config-values-row">
      <td>
        <Link to={`/admin/municipalities/${municipality.municipality_id}`}>{municipality.municipality_name}</Link>
        {(inputError || updateMutation.error) && (
          <div className="small text-danger">{inputError || errorMessage(updateMutation.error)}</div>
        )}
      </td>
      {configKeys.map(({ config_key: key, default_value: defaultValue }) => {
        const stored = municipality.values[key]
        return (
          <td key={key}>
            <Form.Control
              size="sm"
              aria-label={`${municipality.municipality_name} ${key}`}
              value={key in draft ? draft[key] : formatConfigValue(stored)}
              placeholder={`Default: ${formatConfigValue(defaultValue)}`}
              className={stored === null || stored === undefined ? 'border-warning' : ''}
              onChange={(e) => {
                const { value } = e.target
                setDraft((prev) => ({ ...prev, [key]: value }))
              }}
            />
          </td>
        )
      })}
      <td style={{ whiteSpace: 'nowrap' }}>
        <ActionButton
          label="Save"
          onClick={handleSave}
          disabled={!dirty}
          loading={updateMutation.isLoading}
          loadingLabel="Saving"
        />
      </td>
    </tr>
  )
}

const MunicipalityValuesTable = ({ analyticId, configKeys, municipalities }) => {
  if (configKeys.length === 0) return null
  if (municipalities.length === 0) {
    return <Alert variant="info">No municipality has this analytic’s module, so there are no values to set.</Alert>
  }

  return (
    <div className="table-responsive">
      <Table bordered size="sm" className="align-middle">
        <thead>
          <tr>
            <th>Municipality</th>
            {configKeys.map(({ config_key: key }) => (
              <th key={key}>
                <code>{key}</code>
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {municipalities.map((municipality) => (
            <MunicipalityValuesRow
              key={municipality.municipality_id}
              analyticId={analyticId}
              municipality={municipality}
              configKeys={configKeys}
            />
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const AnalyticConfigTab = ({ analytic }) => {
  const { data, isLoading, error } = useAdminAnalyticConfigKeys({ analyticId: analytic.id })

  if (isLoading) return <CenteredSpinner />
  if (error) return <Alert variant="danger">Error loading config variables: {errorMessage(error)}</Alert>

  const configKeys = data?.config_keys || []

  return (
    <>
      <Card className="mb-4">
        <Card.Header>
          <h3 className="h5 mb-0">Config variables</h3>
        </Card.Header>
        <Card.Body>
          {!data.keys_editable && (
            <Alert variant="info" className="small">
              This is a custom analytic: its config variables are the tunable parameters of its recipe, and the
              default shown is the recipe’s current value. Change them in the <Link to={`/admin/municipalities/${analytic.municipality_id}/custom-analytics`}>recipe builder</Link>.
            </Alert>
          )}
          {data.keys_editable && <CodeSync code={data.code} configKeys={configKeys} />}
          <ConfigKeysTable analyticId={analytic.id} configKeys={configKeys} />
          {data.keys_editable && (
            <div className="border-top pt-3 mt-3">
              <AddConfigKeyForm analyticId={analytic.id} availableKeys={data.available_keys || []} />
            </div>
          )}
        </Card.Body>
      </Card>

      {data.keys_editable && configKeys.length > 0 && (
        <Card>
          <Card.Header>
            <h3 className="h5 mb-0">Values by municipality</h3>
          </Card.Header>
          <Card.Body>
            <p className="small text-muted">
              These are the same values municipalities edit on the analytic page in the app. A value belongs to the
              municipality and key, so changing a key another analytic also reads changes it there too. Arrays are
              comma-separated. Outlined cells have no value set and run with the key’s default.
            </p>
            <MunicipalityValuesTable
              analyticId={analytic.id}
              configKeys={configKeys}
              municipalities={data.municipalities || []}
            />
          </Card.Body>
        </Card>
      )}
    </>
  )
}

export default AnalyticConfigTab
